import React, { useEffect, useMemo } from 'react';
import { Card, Spin, Button, Input, DatePicker, Select, Divider, Typography } from 'antd';
import type {
  VisaApplicationCreatePayload,
  VisaApplicationUpdatePayload,
} from '../types/visa-application.types';
import {
  VisaStatusEnum,
  MaritalStatusEnum,
  FirstLanguageEnum,
  GenderEnum,
  NationalityEnum,
  PassportGenderEnum,
  WorkTypeEnum,
  ApplicationTypeEnum,
  TicketStatusEnum,
  ApprovalStatusEnum,
  EscalationLevelEnum,
  RootCauseCategoryEnum,
} from '../types/visa-application.types';
import { useTranslation } from 'react-i18next';
import { DefaultValues } from 'react-hook-form';
import { ValidationError } from '../../../../../types/api-response.types';
import { useForm, Controller } from 'react-hook-form';
import FormBuilder, { FormField } from '../../../../common/FormBuilder';
import { SaveOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useStatusList } from '../../statuses/hooks/use-status.hook';

type FormValues = VisaApplicationCreatePayload | VisaApplicationUpdatePayload;

interface VisaApplicationFormProps {
  defaultValues?: DefaultValues<FormValues>;
  onFormSubmit: (data: FormValues, resetForm: () => void) => void;
  isLoading: boolean;
  validationErrors?: ValidationError[];
}

function VisaApplicationFormComponent({
  defaultValues,
  onFormSubmit,
  isLoading = false,
  validationErrors = [],
}: VisaApplicationFormProps) {
  const { t } = useTranslation();
  const { data: statusesData } = useStatusList({ page: 1, limit: 100 });
  const statuses = statusesData?.docs || [];

  const statusOptions = useMemo(() => {
    return statuses.map((status) => ({
      label: status.statusCode,
      value: status.id,
    }));
  }, [statuses]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
  });

  const topFields: FormField<FormValues>[] = [
    {
      name: 'passportNumber' as any,
      label: t('visaApplications.passportNumber.label'),
      required: false,
      errorMsg: (errors as any)?.passportNumber?.message,
      render: (field) => (
        <Input {...field} placeholder={t('visaApplications.passportNumber.placeholder')} />
      ),
    },
    {
      name: 'applicationNumber' as any,
      label: t('visaApplications.applicationNumber.label'),
      required: false,
      errorMsg: (errors as any)?.applicationNumber?.message,
      render: (field) => (
        <Input {...field} placeholder={t('visaApplications.applicationNumber.placeholder')} />
      ),
    },
    {
      name: 'visaType' as any,
      label: t('visaApplications.visaType.label'),
      required: false,
      errorMsg: (errors as any)?.visaType?.message,
      render: (field) => (
        <Input {...field} placeholder={t('visaApplications.visaType.placeholder')} />
      ),
    },
    {
      name: 'visaName' as any,
      label: t('visaApplications.visaName.label'),
      required: false,
      errorMsg: (errors as any)?.visaName?.message,
      render: (field) => (
        <Input {...field} placeholder={t('visaApplications.visaName.placeholder')} />
      ),
    },
    {
      name: 'visaSponsor' as any,
      label: t('visaApplications.visaSponsor.label'),
      required: false,
      errorMsg: (errors as any)?.visaSponsor?.message,
      render: (field) => (
        <Input {...field} placeholder={t('visaApplications.visaSponsor.placeholder')} />
      ),
    },
    {
      name: 'visaStatus' as any,
      label: t('visaApplications.visaStatus.label'),
      required: false,
      errorMsg: (errors as any)?.visaStatus?.message,
      render: (field) => (
        <Select
          {...field}
          className="w-100"
          placeholder={t('visaApplications.visaStatus.placeholder')}
          options={statusOptions}
        />
      ),
    },
    {
      name: 'visaIssueDate' as any,
      label: t('visaApplications.visaIssueDate.label'),
      required: false,
      errorMsg: (errors as any)?.visaIssueDate?.message,
      render: (field) => (
        <DatePicker
          value={field.value ? dayjs(field.value as any) : undefined}
          onChange={(d: Dayjs | null) => field.onChange(d?.toDate())}
          className="w-100"
          placeholder={t('visaApplications.visaIssueDate.placeholder')}
        />
      ),
    },
    {
      name: 'visaExpiryDate' as any,
      label: t('visaApplications.visaExpiryDate.label'),
      required: false,
      errorMsg: (errors as any)?.visaExpiryDate?.message,
      render: (field) => (
        <DatePicker
          value={field.value ? dayjs(field.value as any) : undefined}
          onChange={(d: Dayjs | null) => field.onChange(d?.toDate())}
          className="w-100"
          placeholder={t('visaApplications.visaExpiryDate.placeholder')}
        />
      ),
    },
  ];

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues as any);
    }
  }, [defaultValues, reset]);

  const onSubmit = (data: FormValues) => {
    onFormSubmit(data, () => reset());
  };

  return (
    <Spin spinning={isLoading} tip={t('common.submittingForm')}>
      <Card className="shadow-lg">
        <form onSubmit={handleSubmit(onSubmit as any)}>
          <Typography.Title level={4} className="mb-3">
            {t('visaApplications.title')}
          </Typography.Title>
          <FormBuilder
            formFields={topFields}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>{t('common.generalInformation')}</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'mrz' as any,
                label: 'MRZ',
                required: false,
                errorMsg: (errors as any)?.mrz?.message,
                render: (field) => <Input {...field} placeholder="MRZ" />,
              },
              {
                name: 'visaUidNumber' as any,
                label: t('visaApplications.visaUidNumber.label') ?? 'Visa UID',
                required: false,
                errorMsg: (errors as any)?.visaUidNumber?.message,
                render: (field) => <Input {...field} placeholder="Visa UID" />,
              },
              {
                name: 'visaFileNumber' as any,
                label: t('visaApplications.visaFileNumber.label') ?? 'Visa File Number',
                required: false,
                errorMsg: (errors as any)?.visaFileNumber?.message,
                render: (field) => <Input {...field} placeholder="Visa File Number" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Client</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'clientData.clientName' as any,
                label: 'Client Name',
                required: false,
                errorMsg: (errors as any)?.clientData?.clientName?.message,
                render: (field) => <Input {...field} placeholder="Client Name" />,
              },
              {
                name: 'clientData.orgLevel1' as any,
                label: 'Org Level 1',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel1?.message,
                render: (field) => <Input {...field} placeholder="Org Level 1" />,
              },
              {
                name: 'clientData.orgLevel2' as any,
                label: 'Org Level 2',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel2?.message,
                render: (field) => <Input {...field} placeholder="Org Level 2" />,
              },
              {
                name: 'clientData.orgLevel3' as any,
                label: 'Org Level 3',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel3?.message,
                render: (field) => <Input {...field} placeholder="Org Level 3" />,
              },
              {
                name: 'clientData.orgLevel4' as any,
                label: 'Org Level 4',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel4?.message,
                render: (field) => <Input {...field} placeholder="Org Level 4" />,
              },
              {
                name: 'clientData.orgLevel5' as any,
                label: 'Org Level 5',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel5?.message,
                render: (field) => <Input {...field} placeholder="Org Level 5" />,
              },
              {
                name: 'clientData.orgLevel6' as any,
                label: 'Org Level 6',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel6?.message,
                render: (field) => <Input {...field} placeholder="Org Level 6" />,
              },
              {
                name: 'clientData.orgLevel7' as any,
                label: 'Org Level 7',
                required: false,
                errorMsg: (errors as any)?.clientData?.orgLevel7?.message,
                render: (field) => <Input {...field} placeholder="Org Level 7" />,
              },
              {
                name: 'clientData.tradeLicenseNumber' as any,
                label: 'Trade License Number',
                required: false,
                errorMsg: (errors as any)?.clientData?.tradeLicenseNumber?.message,
                render: (field) => <Input {...field} placeholder="Trade License Number" />,
              },
              {
                name: 'clientData.tradeLicenseIssueDate' as any,
                label: 'Trade License Issue Date',
                required: false,
                errorMsg: (errors as any)?.clientData?.tradeLicenseIssueDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Trade License Issue Date"
                  />
                ),
              },
              {
                name: 'clientData.tradeLicenseExpiryDate' as any,
                label: 'Trade License Expiry Date',
                required: false,
                errorMsg: (errors as any)?.clientData?.tradeLicenseExpiryDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Trade License Expiry Date"
                  />
                ),
              },
              {
                name: 'clientData.estCardNumber' as any,
                label: 'Est Card Number',
                required: false,
                errorMsg: (errors as any)?.clientData?.estCardNumber?.message,
                render: (field) => <Input {...field} placeholder="Est Card Number" />,
              },
              {
                name: 'clientData.estCardIssueDate' as any,
                label: 'Est Card Issue Date',
                required: false,
                errorMsg: (errors as any)?.clientData?.estCardIssueDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Est Card Issue Date"
                  />
                ),
              },
              {
                name: 'clientData.estCardExpiryDate' as any,
                label: 'Est Card Expiry Date',
                required: false,
                errorMsg: (errors as any)?.clientData?.estCardExpiryDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Est Card Expiry Date"
                  />
                ),
              },
              {
                name: 'clientData.companyAddress' as any,
                label: 'Company Address',
                required: false,
                errorMsg: (errors as any)?.clientData?.companyAddress?.message,
                render: (field) => <Input {...field} placeholder="Company Address" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Application</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'applicationData.serviceName' as any,
                label: 'Service Name',
                required: false,
                errorMsg: (errors as any)?.applicationData?.serviceName?.message,
                render: (field) => <Input {...field} placeholder="Service Name" />,
              },
              {
                name: 'applicationData.serviceStage' as any,
                label: 'Service Stage',
                required: false,
                errorMsg: (errors as any)?.applicationData?.serviceStage?.message,
                render: (field) => <Input {...field} placeholder="Service Stage" />,
              },
              {
                name: 'applicationData.serviceOutcome' as any,
                label: 'Service Outcome',
                required: false,
                errorMsg: (errors as any)?.applicationData?.serviceOutcome?.message,
                render: (field) => <Input {...field} placeholder="Service Outcome" />,
              },
              {
                name: 'applicationData.triggerTime' as any,
                label: 'Trigger Time',
                required: false,
                errorMsg: (errors as any)?.applicationData?.triggerTime?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Trigger Time"
                  />
                ),
              },
              {
                name: 'applicationData.executionStartTime' as any,
                label: 'Execution Start',
                required: false,
                errorMsg: (errors as any)?.applicationData?.executionStartTime?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Execution Start"
                  />
                ),
              },
              {
                name: 'applicationData.executionEndTime' as any,
                label: 'Execution End',
                required: false,
                errorMsg: (errors as any)?.applicationData?.executionEndTime?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Execution End"
                  />
                ),
              },
              {
                name: 'applicationData.executionDuration' as any,
                label: 'Execution Duration (min)',
                required: false,
                errorMsg: (errors as any)?.applicationData?.executionDuration?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Execution Duration (min)" />
                ),
              },
              {
                name: 'applicationData.documentNumber' as any,
                label: 'Document Number',
                required: false,
                errorMsg: (errors as any)?.applicationData?.documentNumber?.message,
                render: (field) => <Input {...field} placeholder="Document Number" />,
              },
              {
                name: 'applicationData.failureCategory' as any,
                label: 'Failure Category',
                required: false,
                errorMsg: (errors as any)?.applicationData?.failureCategory?.message,
                render: (field) => <Input {...field} placeholder="Failure Category" />,
              },
              {
                name: 'applicationData.failureDetail' as any,
                label: 'Failure Detail',
                required: false,
                errorMsg: (errors as any)?.applicationData?.failureDetail?.message,
                render: (field) => <Input {...field} placeholder="Failure Detail" />,
              },
              {
                name: 'applicationData.failureCount' as any,
                label: 'Failure Count',
                required: false,
                errorMsg: (errors as any)?.applicationData?.failureCount?.message,
                render: (field) => <Input type="number" {...field} placeholder="Failure Count" />,
              },
              {
                name: 'applicationData.machineId' as any,
                label: 'Machine ID',
                required: false,
                errorMsg: (errors as any)?.applicationData?.machineId?.message,
                render: (field) => <Input {...field} placeholder="Machine ID" />,
              },
              {
                name: 'applicationData.incidentId' as any,
                label: 'Incident ID',
                required: false,
                errorMsg: (errors as any)?.applicationData?.incidentId?.message,
                render: (field) => <Input {...field} placeholder="Incident ID" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Employee</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'employeeData.candidateId' as any,
                label: 'Candidate ID',
                required: false,
                errorMsg: (errors as any)?.employeeData?.candidateId?.message,
                render: (field) => <Input {...field} placeholder="Candidate ID" />,
              },
              {
                name: 'employeeData.employeeId' as any,
                label: 'Employee ID',
                required: false,
                errorMsg: (errors as any)?.employeeData?.employeeId?.message,
                render: (field) => <Input {...field} placeholder="Employee ID" />,
              },
              {
                name: 'employeeData.probotId' as any,
                label: 'Probot ID',
                required: false,
                errorMsg: (errors as any)?.employeeData?.probotId?.message,
                render: (field) => <Input {...field} placeholder="Probot ID" />,
              },
              {
                name: 'employeeData.employmentStartDateEstimate' as any,
                label: 'Employment Start (Est)',
                required: false,
                errorMsg: (errors as any)?.employeeData?.employmentStartDateEstimate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Employment Start (Est)"
                  />
                ),
              },
              {
                name: 'employeeData.employmentStartDateActual' as any,
                label: 'Employment Start (Actual)',
                required: false,
                errorMsg: (errors as any)?.employeeData?.employmentStartDateActual?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Employment Start (Actual)"
                  />
                ),
              },
              {
                name: 'employeeData.employmentEndDate' as any,
                label: 'Employment End',
                required: false,
                errorMsg: (errors as any)?.employeeData?.employmentEndDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Employment End"
                  />
                ),
              },
              {
                name: 'employeeData.addressHome' as any,
                label: 'Home Address',
                required: false,
                errorMsg: (errors as any)?.employeeData?.addressHome?.message,
                render: (field) => <Input {...field} placeholder="Home Address" />,
              },
              {
                name: 'employeeData.addressLocal' as any,
                label: 'Local Address',
                required: false,
                errorMsg: (errors as any)?.employeeData?.addressLocal?.message,
                render: (field) => <Input {...field} placeholder="Local Address" />,
              },
              {
                name: 'employeeData.phoneHome' as any,
                label: 'Home Phone',
                required: false,
                errorMsg: (errors as any)?.employeeData?.phoneHome?.message,
                render: (field) => <Input {...field} placeholder="Home Phone" />,
              },
              {
                name: 'employeeData.educationLevel' as any,
                label: 'Education Level',
                required: false,
                errorMsg: (errors as any)?.employeeData?.educationLevel?.message,
                render: (field) => <Input {...field} placeholder="Education Level" />,
              },
              {
                name: 'employeeData.educationDescription' as any,
                label: 'Education Description',
                required: false,
                errorMsg: (errors as any)?.employeeData?.educationDescription?.message,
                render: (field) => <Input {...field} placeholder="Education Description" />,
              },
              {
                name: 'employeeData.maritalStatus' as any,
                label: 'Marital Status',
                required: false,
                errorMsg: (errors as any)?.employeeData?.maritalStatus?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Marital Status"
                    options={[
                      { label: 'Single', value: MaritalStatusEnum.SINGLE },
                      { label: 'Married', value: MaritalStatusEnum.MARRIED },
                      { label: 'Divorced', value: MaritalStatusEnum.DIVORCED },
                      { label: 'Widowed', value: MaritalStatusEnum.WIDOWED },
                      { label: 'Separated', value: MaritalStatusEnum.SEPARATED },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.firstLanguage' as any,
                label: 'First Language',
                required: false,
                errorMsg: (errors as any)?.employeeData?.firstLanguage?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="First Language"
                    options={[
                      { label: 'English', value: FirstLanguageEnum.ENGLISH },
                      { label: 'Arabic', value: FirstLanguageEnum.ARABIC },
                      { label: 'Other', value: FirstLanguageEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.numberOfChildren' as any,
                label: 'Number of Children',
                required: false,
                errorMsg: (errors as any)?.employeeData?.numberOfChildren?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Number of Children" />
                ),
              },
              {
                name: 'employeeData.motherName' as any,
                label: 'Mother Name',
                required: false,
                errorMsg: (errors as any)?.employeeData?.motherName?.message,
                render: (field) => <Input {...field} placeholder="Mother Name" />,
              },
              {
                name: 'employeeData.fatherName' as any,
                label: 'Father Name',
                required: false,
                errorMsg: (errors as any)?.employeeData?.fatherName?.message,
                render: (field) => <Input {...field} placeholder="Father Name" />,
              },
              {
                name: 'employeeData.typedFirstName' as any,
                label: 'Typed First Name',
                required: false,
                errorMsg: (errors as any)?.employeeData?.typedFirstName?.message,
                render: (field) => <Input {...field} placeholder="Typed First Name" />,
              },
              {
                name: 'employeeData.typedMiddleName' as any,
                label: 'Typed Middle Name',
                required: false,
                errorMsg: (errors as any)?.employeeData?.typedMiddleName?.message,
                render: (field) => <Input {...field} placeholder="Typed Middle Name" />,
              },
              {
                name: 'employeeData.typedLastName' as any,
                label: 'Typed Last Name',
                required: false,
                errorMsg: (errors as any)?.employeeData?.typedLastName?.message,
                render: (field) => <Input {...field} placeholder="Typed Last Name" />,
              },
              {
                name: 'employeeData.typedDateOfBirth' as any,
                label: 'Date of Birth',
                required: false,
                errorMsg: (errors as any)?.employeeData?.typedDateOfBirth?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Date of Birth"
                  />
                ),
              },
              {
                name: 'employeeData.typedGender' as any,
                label: 'Gender',
                required: false,
                errorMsg: (errors as any)?.employeeData?.typedGender?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Gender"
                    options={[
                      { label: 'Male', value: GenderEnum.MALE },
                      { label: 'Female', value: GenderEnum.FEMALE },
                      { label: 'Other', value: GenderEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.typedNationality' as any,
                label: 'Nationality',
                required: false,
                errorMsg: (errors as any)?.employeeData?.typedNationality?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Nationality"
                    options={[
                      { label: 'UAE', value: NationalityEnum.UAE },
                      { label: 'India', value: NationalityEnum.INDIA },
                      { label: 'Pakistan', value: NationalityEnum.PAKISTAN },
                      { label: 'Bangladesh', value: NationalityEnum.BANGLADESH },
                      { label: 'Other', value: NationalityEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.biodataVerified' as any,
                label: 'Biodata Verified',
                required: false,
                errorMsg: (errors as any)?.employeeData?.biodataVerified?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Biodata Verified"
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.companyDesignation' as any,
                label: 'Company Designation',
                required: false,
                errorMsg: (errors as any)?.employeeData?.companyDesignation?.message,
                render: (field) => <Input {...field} placeholder="Company Designation" />,
              },
              {
                name: 'employeeData.mappedDesignation' as any,
                label: 'Mapped Designation',
                required: false,
                errorMsg: (errors as any)?.employeeData?.mappedDesignation?.message,
                render: (field) => <Input {...field} placeholder="Mapped Designation" />,
              },
              {
                name: 'employeeData.workType' as any,
                label: 'Work Type',
                required: false,
                errorMsg: (errors as any)?.employeeData?.workType?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Work Type"
                    options={[
                      { label: 'Full-time', value: WorkTypeEnum.FULL_TIME },
                      { label: 'Part-time', value: WorkTypeEnum.PART_TIME },
                      { label: 'Contract', value: WorkTypeEnum.CONTRACT },
                      { label: 'Internship', value: WorkTypeEnum.INTERNSHIP },
                      { label: 'Freelance', value: WorkTypeEnum.FREELANCE },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.previousNationality' as any,
                label: 'Previous Nationality',
                required: false,
                errorMsg: (errors as any)?.employeeData?.previousNationality?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Previous Nationality"
                    options={[
                      { label: 'UAE', value: NationalityEnum.UAE },
                      { label: 'India', value: NationalityEnum.INDIA },
                      { label: 'Pakistan', value: NationalityEnum.PAKISTAN },
                      { label: 'Bangladesh', value: NationalityEnum.BANGLADESH },
                      { label: 'Other', value: NationalityEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.askingForAddressInUae' as any,
                label: 'Need Address in UAE?',
                required: false,
                errorMsg: (errors as any)?.employeeData?.askingForAddressInUae?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Need Address in UAE?"
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                ),
              },
              {
                name: 'employeeData.askingForMobileNumber' as any,
                label: 'Need Mobile Number?',
                required: false,
                errorMsg: (errors as any)?.employeeData?.askingForMobileNumber?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Need Mobile Number?"
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                ),
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Passport</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'passportData.passportFirstName' as any,
                label: 'Passport First Name',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportFirstName?.message,
                render: (field) => <Input {...field} placeholder="Passport First Name" />,
              },
              {
                name: 'passportData.passportMiddleName' as any,
                label: 'Passport Middle Name',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportMiddleName?.message,
                render: (field) => <Input {...field} placeholder="Passport Middle Name" />,
              },
              {
                name: 'passportData.passportLastName' as any,
                label: 'Passport Last Name',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportLastName?.message,
                render: (field) => <Input {...field} placeholder="Passport Last Name" />,
              },
              {
                name: 'passportData.passportIssueDate' as any,
                label: 'Passport Issue Date',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportIssueDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Passport Issue Date"
                  />
                ),
              },
              {
                name: 'passportData.passportExpiryDate' as any,
                label: 'Passport Expiry Date',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportExpiryDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Passport Expiry Date"
                  />
                ),
              },
              {
                name: 'passportData.passportDateOfBirth' as any,
                label: 'Date of Birth',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportDateOfBirth?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Date of Birth"
                  />
                ),
              },
              {
                name: 'passportData.passportGender' as any,
                label: 'Passport Gender',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportGender?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Passport Gender"
                    options={[
                      { label: 'M', value: PassportGenderEnum.M },
                      { label: 'F', value: PassportGenderEnum.F },
                      { label: 'O', value: PassportGenderEnum.O },
                    ]}
                  />
                ),
              },
              {
                name: 'passportData.passportNationality' as any,
                label: 'Passport Nationality',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportNationality?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Passport Nationality"
                    options={[
                      { label: 'UAE', value: NationalityEnum.UAE },
                      { label: 'India', value: NationalityEnum.INDIA },
                      { label: 'Pakistan', value: NationalityEnum.PAKISTAN },
                      { label: 'Bangladesh', value: NationalityEnum.BANGLADESH },
                      { label: 'Other', value: NationalityEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'passportData.passportIssuingAuthority' as any,
                label: 'Issuing Authority',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportIssuingAuthority?.message,
                render: (field) => <Input {...field} placeholder="Issuing Authority" />,
              },
              {
                name: 'passportData.passportIssuingCountry' as any,
                label: 'Issuing Country',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportIssuingCountry?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Issuing Country"
                    options={[
                      { label: 'UAE', value: NationalityEnum.UAE },
                      { label: 'India', value: NationalityEnum.INDIA },
                      { label: 'Pakistan', value: NationalityEnum.PAKISTAN },
                      { label: 'Bangladesh', value: NationalityEnum.BANGLADESH },
                      { label: 'Other', value: NationalityEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'passportData.passportPlaceOfBirth' as any,
                label: 'Place of Birth',
                required: false,
                errorMsg: (errors as any)?.passportData?.passportPlaceOfBirth?.message,
                render: (field) => <Input {...field} placeholder="Place of Birth" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Emirates ID</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'eidData.eidFirstName' as any,
                label: 'EID First Name',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidFirstName?.message,
                render: (field) => <Input {...field} placeholder="EID First Name" />,
              },
              {
                name: 'eidData.eidMiddleName' as any,
                label: 'EID Middle Name',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidMiddleName?.message,
                render: (field) => <Input {...field} placeholder="EID Middle Name" />,
              },
              {
                name: 'eidData.eidLastName' as any,
                label: 'EID Last Name',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidLastName?.message,
                render: (field) => <Input {...field} placeholder="EID Last Name" />,
              },
              {
                name: 'eidData.eidNumber' as any,
                label: 'EID Number',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidNumber?.message,
                render: (field) => <Input {...field} placeholder="EID Number" />,
              },
              {
                name: 'eidData.eidIssueDate' as any,
                label: 'EID Issue Date',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidIssueDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="EID Issue Date"
                  />
                ),
              },
              {
                name: 'eidData.eidExpiryDate' as any,
                label: 'EID Expiry Date',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidExpiryDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="EID Expiry Date"
                  />
                ),
              },
              {
                name: 'eidData.eidDateOfBirth' as any,
                label: 'Date of Birth',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidDateOfBirth?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Date of Birth"
                  />
                ),
              },
              {
                name: 'eidData.eidGender' as any,
                label: 'Gender',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidGender?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Gender"
                    options={[
                      { label: 'Male', value: GenderEnum.MALE },
                      { label: 'Female', value: GenderEnum.FEMALE },
                      { label: 'Other', value: GenderEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'eidData.eidNationality' as any,
                label: 'Nationality',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidNationality?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Nationality"
                    options={[
                      { label: 'UAE', value: 'UAE' },
                      { label: 'India', value: 'India' },
                      { label: 'Pakistan', value: 'Pakistan' },
                      { label: 'Bangladesh', value: 'Bangladesh' },
                      { label: 'Other', value: 'Other' },
                    ]}
                  />
                ),
              },
              {
                name: 'eidData.eidPlaceIssued' as any,
                label: 'Place Issued',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidPlaceIssued?.message,
                render: (field) => <Input {...field} placeholder="Place Issued" />,
              },
              {
                name: 'eidData.eidOccupation' as any,
                label: 'Occupation',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidOccupation?.message,
                render: (field) => <Input {...field} placeholder="Occupation" />,
              },
              {
                name: 'eidData.eidCardNumber' as any,
                label: 'EID Card Number',
                required: false,
                errorMsg: (errors as any)?.eidData?.eidCardNumber?.message,
                render: (field) => <Input {...field} placeholder="EID Card Number" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Visa</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'visaData.visaEmirate' as any,
                label: 'Visa Emirate',
                required: false,
                errorMsg: (errors as any)?.visaData?.visaEmirate?.message,
                render: (field) => <Input {...field} placeholder="Visa Emirate" />,
              },
              {
                name: 'visaData.visaDesignation' as any,
                label: 'Visa Designation',
                required: false,
                errorMsg: (errors as any)?.visaData?.visaDesignation?.message,
                render: (field) => <Input {...field} placeholder="Visa Designation" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Driver's License</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'driversLicense.dlFullName' as any,
                label: 'Full Name',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlFullName?.message,
                render: (field) => <Input {...field} placeholder="Full Name" />,
              },
              {
                name: 'driversLicense.dlCategory1' as any,
                label: 'Category 1',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlCategory1?.message,
                render: (field) => <Input {...field} placeholder="Category 1" />,
              },
              {
                name: 'driversLicense.dlCategory2' as any,
                label: 'Category 2',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlCategory2?.message,
                render: (field) => <Input {...field} placeholder="Category 2" />,
              },
              {
                name: 'driversLicense.dlNumber' as any,
                label: 'License Number',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlNumber?.message,
                render: (field) => <Input {...field} placeholder="License Number" />,
              },
              {
                name: 'driversLicense.dlIssueDate' as any,
                label: 'Issue Date',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlIssueDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Issue Date"
                  />
                ),
              },
              {
                name: 'driversLicense.dlExpiryDate' as any,
                label: 'Expiry Date',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlExpiryDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Expiry Date"
                  />
                ),
              },
              {
                name: 'driversLicense.dlDateOfBirth' as any,
                label: 'Date of Birth',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlDateOfBirth?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Date of Birth"
                  />
                ),
              },
              {
                name: 'driversLicense.dlPlaceIssued' as any,
                label: 'Place Issued',
                required: false,
                errorMsg: (errors as any)?.driversLicense?.dlPlaceIssued?.message,
                render: (field) => <Input {...field} placeholder="Place Issued" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Contract</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'contract.noticePeriod' as any,
                label: 'Notice Period (days)',
                required: false,
                errorMsg: (errors as any)?.contract?.noticePeriod?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Notice Period (days)" />
                ),
              },
              {
                name: 'contract.probationPeriod' as any,
                label: 'Probation Period (days)',
                required: false,
                errorMsg: (errors as any)?.contract?.probationPeriod?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Probation Period (days)" />
                ),
              },
              {
                name: 'contract.restDays' as any,
                label: 'Rest Days',
                required: false,
                errorMsg: (errors as any)?.contract?.restDays?.message,
                render: (field) => <Input type="number" {...field} placeholder="Rest Days" />,
              },
              {
                name: 'contract.basicSalaryAmount' as any,
                label: 'Basic Salary Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.basicSalaryAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Basic Salary Amount" />
                ),
              },
              {
                name: 'contract.basicSalaryFrequency' as any,
                label: 'Basic Salary Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.basicSalaryFrequency?.message,
                render: (field) => <Input {...field} placeholder="Basic Salary Frequency" />,
              },
              {
                name: 'contract.housingAmount' as any,
                label: 'Housing Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.housingAmount?.message,
                render: (field) => <Input type="number" {...field} placeholder="Housing Amount" />,
              },
              {
                name: 'contract.housingFrequency' as any,
                label: 'Housing Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.housingFrequency?.message,
                render: (field) => <Input {...field} placeholder="Housing Frequency" />,
              },
              {
                name: 'contract.housingProvidedByCompany' as any,
                label: 'Housing Provided by Company?',
                required: false,
                errorMsg: (errors as any)?.contract?.housingProvidedByCompany?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Housing Provided by Company?"
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                ),
              },
              {
                name: 'contract.transportAllowanceAmount' as any,
                label: 'Transport Allowance Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.transportAllowanceAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Transport Allowance Amount" />
                ),
              },
              {
                name: 'contract.transportAllowanceFrequency' as any,
                label: 'Transport Allowance Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.transportAllowanceFrequency?.message,
                render: (field) => <Input {...field} placeholder="Transport Allowance Frequency" />,
              },
              {
                name: 'contract.otherAllowanceAmount' as any,
                label: 'Other Allowance Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.otherAllowanceAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Other Allowance Amount" />
                ),
              },
              {
                name: 'contract.otherAllowanceFrequency' as any,
                label: 'Other Allowance Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.otherAllowanceFrequency?.message,
                render: (field) => <Input {...field} placeholder="Other Allowance Frequency" />,
              },
              {
                name: 'contract.childrenSchoolAllowanceAmount' as any,
                label: 'Children School Allowance Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.childrenSchoolAllowanceAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Children School Allowance Amount" />
                ),
              },
              {
                name: 'contract.telephoneAllowanceAmount' as any,
                label: 'Telephone Allowance Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.telephoneAllowanceAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Telephone Allowance Amount" />
                ),
              },
              {
                name: 'contract.telephoneAllowanceFrequency' as any,
                label: 'Telephone Allowance Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.telephoneAllowanceFrequency?.message,
                render: (field) => <Input {...field} placeholder="Telephone Allowance Frequency" />,
              },
              {
                name: 'contract.ticketAllowanceAmount' as any,
                label: 'Ticket Allowance Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.ticketAllowanceAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Ticket Allowance Amount" />
                ),
              },
              {
                name: 'contract.ticketAllowanceFrequency' as any,
                label: 'Ticket Allowance Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.ticketAllowanceFrequency?.message,
                render: (field) => <Input {...field} placeholder="Ticket Allowance Frequency" />,
              },
              {
                name: 'contract.commissionAmount' as any,
                label: 'Commission Amount',
                required: false,
                errorMsg: (errors as any)?.contract?.commissionAmount?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Commission Amount" />
                ),
              },
              {
                name: 'contract.commissionFrequency' as any,
                label: 'Commission Frequency',
                required: false,
                errorMsg: (errors as any)?.contract?.commissionFrequency?.message,
                render: (field) => <Input {...field} placeholder="Commission Frequency" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Recruiter</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'recruiterInfo.recruiterName' as any,
                label: 'Recruiter Name',
                required: false,
                errorMsg: (errors as any)?.recruiterInfo?.recruiterName?.message,
                render: (field) => <Input {...field} placeholder="Recruiter Name" />,
              },
              {
                name: 'recruiterInfo.recruiterEmail' as any,
                label: 'Recruiter Email',
                required: false,
                errorMsg: (errors as any)?.recruiterInfo?.recruiterEmail?.message,
                render: (field) => <Input {...field} placeholder="Recruiter Email" />,
              },
              {
                name: 'recruiterInfo.recruiterTelephone' as any,
                label: 'Recruiter Telephone',
                required: false,
                errorMsg: (errors as any)?.recruiterInfo?.recruiterTelephone?.message,
                render: (field) => <Input {...field} placeholder="Recruiter Telephone" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Religion</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'religion.religionCode' as any,
                label: 'Religion Code',
                required: false,
                errorMsg: (errors as any)?.religion?.religionCode?.message,
                render: (field) => <Input {...field} placeholder="Religion Code" />,
              },
              {
                name: 'religion.religionName' as any,
                label: 'Religion Name',
                required: false,
                errorMsg: (errors as any)?.religion?.religionName?.message,
                render: (field) => <Input {...field} placeholder="Religion Name" />,
              },
              {
                name: 'religion.sectCode' as any,
                label: 'Sect Code',
                required: false,
                errorMsg: (errors as any)?.religion?.sectCode?.message,
                render: (field) => <Input {...field} placeholder="Sect Code" />,
              },
              {
                name: 'religion.sectName' as any,
                label: 'Sect Name',
                required: false,
                errorMsg: (errors as any)?.religion?.sectName?.message,
                render: (field) => <Input {...field} placeholder="Sect Name" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Snapshot</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'snapshotTable.applicationStatus' as any,
                label: 'Application Status',
                required: false,
                errorMsg: (errors as any)?.snapshotTable?.applicationStatus?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Application Status"
                    options={[
                      { label: 'Pending', value: VisaStatusEnum.PENDING },
                      { label: 'Processed', value: VisaStatusEnum.PROCESSED },
                      { label: 'Failed', value: VisaStatusEnum.FAILED },
                      { label: 'In Progress', value: VisaStatusEnum.IN_PROGRESS },
                    ]}
                  />
                ),
              },
              {
                name: 'snapshotTable.applicationType' as any,
                label: 'Application Type',
                required: false,
                errorMsg: (errors as any)?.snapshotTable?.applicationType?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Application Type"
                    options={[
                      { label: 'Single', value: ApplicationTypeEnum.SINGLE },
                      { label: 'Multiple', value: ApplicationTypeEnum.MULTIPLE },
                    ]}
                  />
                ),
              },
              {
                name: 'snapshotTable.applicationCost' as any,
                label: 'Application Cost',
                required: false,
                errorMsg: (errors as any)?.snapshotTable?.applicationCost?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Application Cost" />
                ),
              },
              {
                name: 'snapshotTable.createdTimestamp' as any,
                label: 'Created Timestamp',
                required: false,
                errorMsg: (errors as any)?.snapshotTable?.createdTimestamp?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Created Timestamp"
                  />
                ),
              },
              {
                name: 'snapshotTable.statusTimestamp' as any,
                label: 'Status Timestamp',
                required: false,
                errorMsg: (errors as any)?.snapshotTable?.statusTimestamp?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Status Timestamp"
                  />
                ),
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Mapping</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'mapping.profCode' as any,
                label: 'Prof Code',
                required: false,
                errorMsg: (errors as any)?.mapping?.profCode?.message,
                render: (field) => <Input {...field} placeholder="Prof Code" />,
              },
              {
                name: 'mapping.profContract' as any,
                label: 'Prof Contract',
                required: false,
                errorMsg: (errors as any)?.mapping?.profContract?.message,
                render: (field) => <Input {...field} placeholder="Prof Contract" />,
              },
              {
                name: 'mapping.profGdrfa' as any,
                label: 'Prof GDRFA',
                required: false,
                errorMsg: (errors as any)?.mapping?.profGdrfa?.message,
                render: (field) => <Input {...field} placeholder="Prof GDRFA" />,
              },
              {
                name: 'mapping.profGdrfaCode' as any,
                label: 'Prof GDRFA Code',
                required: false,
                errorMsg: (errors as any)?.mapping?.profGdrfaCode?.message,
                render: (field) => <Input {...field} placeholder="Prof GDRFA Code" />,
              },
              {
                name: 'mapping.profHrms' as any,
                label: 'Prof HRMS',
                required: false,
                errorMsg: (errors as any)?.mapping?.profHrms?.message,
                render: (field) => <Input {...field} placeholder="Prof HRMS" />,
              },
              {
                name: 'mapping.profPreferred' as any,
                label: 'Prof Preferred',
                required: false,
                errorMsg: (errors as any)?.mapping?.profPreferred?.message,
                render: (field) => <Input {...field} placeholder="Prof Preferred" />,
              },
              {
                name: 'mapping.profBu' as any,
                label: 'Prof BU',
                required: false,
                errorMsg: (errors as any)?.mapping?.profBu?.message,
                render: (field) => <Input {...field} placeholder="Prof BU" />,
              },
              {
                name: 'mapping.deptCode' as any,
                label: 'Dept Code',
                required: false,
                errorMsg: (errors as any)?.mapping?.deptCode?.message,
                render: (field) => <Input {...field} placeholder="Dept Code" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Flight Info</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'flightInfo.airlineName' as any,
                label: 'Airline Name',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.airlineName?.message,
                render: (field) => <Input {...field} placeholder="Airline Name" />,
              },
              {
                name: 'flightInfo.flightNumber' as any,
                label: 'Flight Number',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.flightNumber?.message,
                render: (field) => <Input {...field} placeholder="Flight Number" />,
              },
              {
                name: 'flightInfo.departureAirport' as any,
                label: 'Departure Airport',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.departureAirport?.message,
                render: (field) => <Input {...field} placeholder="Departure Airport" />,
              },
              {
                name: 'flightInfo.arrivalAirport' as any,
                label: 'Arrival Airport',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.arrivalAirport?.message,
                render: (field) => <Input {...field} placeholder="Arrival Airport" />,
              },
              {
                name: 'flightInfo.arrivalDate' as any,
                label: 'Arrival Date',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.arrivalDate?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Arrival Date"
                  />
                ),
              },
              {
                name: 'flightInfo.arrivalTime' as any,
                label: 'Arrival Time',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.arrivalTime?.message,
                render: (field) => <Input {...field} placeholder="Arrival Time" />,
              },
              {
                name: 'flightInfo.bookingRef' as any,
                label: 'Booking Ref',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.bookingRef?.message,
                render: (field) => <Input {...field} placeholder="Booking Ref" />,
              },
              {
                name: 'flightInfo.lastUpdated' as any,
                label: 'Last Updated',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.lastUpdated?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Last Updated"
                  />
                ),
              },
              {
                name: 'flightInfo.updatedBy' as any,
                label: 'Updated By',
                required: false,
                errorMsg: (errors as any)?.flightInfo?.updatedBy?.message,
                render: (field) => <Input {...field} placeholder="Updated By" />,
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Divider className="my-6" />
          <Typography.Title level={5}>Ticket Management</Typography.Title>
          <FormBuilder
            formFields={[
              {
                name: 'ticketManagement.incidentNumber' as any,
                label: 'Incident Number',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.incidentNumber?.message,
                render: (field) => <Input {...field} placeholder="Incident Number" />,
              },
              {
                name: 'ticketManagement.subject' as any,
                label: 'Subject',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.subject?.message,
                render: (field) => <Input {...field} placeholder="Subject" />,
              },
              {
                name: 'ticketManagement.description' as any,
                label: 'Description',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.description?.message,
                render: (field) => <Input {...field} placeholder="Description" />,
              },
              {
                name: 'ticketManagement.requesterOrganisation' as any,
                label: 'Requester Organisation',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.requesterOrganisation?.message,
                render: (field) => <Input {...field} placeholder="Requester Organisation" />,
              },
              {
                name: 'ticketManagement.requesterName' as any,
                label: 'Requester Name',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.requesterName?.message,
                render: (field) => <Input {...field} placeholder="Requester Name" />,
              },
              {
                name: 'ticketManagement.requesterEmail' as any,
                label: 'Requester Email',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.requesterEmail?.message,
                render: (field) => <Input {...field} placeholder="Requester Email" />,
              },
              {
                name: 'ticketManagement.requesterPhone' as any,
                label: 'Requester Phone',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.requesterPhone?.message,
                render: (field) => <Input {...field} placeholder="Requester Phone" />,
              },
              {
                name: 'ticketManagement.status' as any,
                label: 'Status',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.status?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Status"
                    options={[
                      { label: 'Open', value: TicketStatusEnum.OPEN },
                      { label: 'Pending', value: TicketStatusEnum.PENDING },
                      { label: 'Completed', value: TicketStatusEnum.COMPLETED },
                    ]}
                  />
                ),
              },
              {
                name: 'ticketManagement.probotId' as any,
                label: 'Probot ID',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.probotId?.message,
                render: (field) => <Input {...field} placeholder="Probot ID" />,
              },
              {
                name: 'ticketManagement.clientPriority' as any,
                label: 'Client Priority',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.clientPriority?.message,
                render: (field) => <Input {...field} placeholder="Client Priority" />,
              },
              {
                name: 'ticketManagement.probotPriority' as any,
                label: 'Probot Priority',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.probotPriority?.message,
                render: (field) => <Input {...field} placeholder="Probot Priority" />,
              },
              {
                name: 'ticketManagement.assignedTo' as any,
                label: 'Assigned To',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.assignedTo?.message,
                render: (field) => <Input {...field} placeholder="Assigned To" />,
              },
              {
                name: 'ticketManagement.relatedServiceCode' as any,
                label: 'Related Service Code',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.relatedServiceCode?.message,
                render: (field) => <Input {...field} placeholder="Related Service Code" />,
              },
              {
                name: 'ticketManagement.resolutionSummary' as any,
                label: 'Resolution Summary',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.resolutionSummary?.message,
                render: (field) => <Input {...field} placeholder="Resolution Summary" />,
              },
              {
                name: 'ticketManagement.approvalStatus' as any,
                label: 'Approval Status',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.approvalStatus?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Approval Status"
                    options={[
                      { label: 'Pending', value: ApprovalStatusEnum.PENDING },
                      { label: 'Approved', value: ApprovalStatusEnum.APPROVED },
                      { label: 'Rejected', value: ApprovalStatusEnum.REJECTED },
                    ]}
                  />
                ),
              },
              {
                name: 'ticketManagement.escalationLevel' as any,
                label: 'Escalation Level',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.escalationLevel?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Escalation Level"
                    options={[
                      { label: 'Low', value: EscalationLevelEnum.LOW },
                      { label: 'Medium', value: EscalationLevelEnum.MEDIUM },
                      { label: 'High', value: EscalationLevelEnum.HIGH },
                    ]}
                  />
                ),
              },
              {
                name: 'ticketManagement.attachments' as any,
                label: 'Attachments',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.attachments?.message,
                render: (field) => <Input {...field} placeholder="Attachments" />,
              },
              {
                name: 'ticketManagement.feedbackRating' as any,
                label: 'Feedback Rating',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.feedbackRating?.message,
                render: (field) => <Input type="number" {...field} placeholder="Feedback Rating" />,
              },
              {
                name: 'ticketManagement.rootCauseCategory' as any,
                label: 'Root Cause Category',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.rootCauseCategory?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Root Cause Category"
                    options={[
                      { label: 'Technical', value: RootCauseCategoryEnum.TECHNICAL },
                      { label: 'User', value: RootCauseCategoryEnum.USER },
                      { label: 'Process', value: RootCauseCategoryEnum.PROCESS },
                      { label: 'Other', value: RootCauseCategoryEnum.OTHER },
                    ]}
                  />
                ),
              },
              {
                name: 'ticketManagement.linkedTickets' as any,
                label: 'Linked Tickets',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.linkedTickets?.message,
                render: (field) => <Input {...field} placeholder="Linked Tickets" />,
              },
              {
                name: 'ticketManagement.automationFlag' as any,
                label: 'Automation?',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.automationFlag?.message,
                render: (field) => (
                  <Select
                    {...field}
                    className="w-100"
                    placeholder="Automation?"
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />
                ),
              },
              {
                name: 'ticketManagement.openTimestamp' as any,
                label: 'Open Timestamp',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.openTimestamp?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Open Timestamp"
                  />
                ),
              },
              {
                name: 'ticketManagement.lastUpdateTimestamp' as any,
                label: 'Last Update',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.lastUpdateTimestamp?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Last Update"
                  />
                ),
              },
              {
                name: 'ticketManagement.closedTimestamp' as any,
                label: 'Closed Timestamp',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.closedTimestamp?.message,
                render: (field) => (
                  <DatePicker
                    value={field.value ? dayjs(field.value as any) : undefined}
                    onChange={(d) => field.onChange(d?.toDate())}
                    className="w-100"
                    placeholder="Closed Timestamp"
                  />
                ),
              },
              {
                name: 'ticketManagement.timeToFirstResponse' as any,
                label: 'Time to First Response (min)',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.timeToFirstResponse?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Time to First Response (min)" />
                ),
              },
              {
                name: 'ticketManagement.timeToResolution' as any,
                label: 'Time to Resolution (min)',
                required: false,
                errorMsg: (errors as any)?.ticketManagement?.timeToResolution?.message,
                render: (field) => (
                  <Input type="number" {...field} placeholder="Time to Resolution (min)" />
                ),
              },
            ]}
            control={control}
            errors={errors}
            onSubmit={handleSubmit(onSubmit as any)}
            submitButtonText={t('common.save')}
            isLoading={isLoading}
            columns={3}
            showSubmitButton={false}
          />

          <Button
            type="primary"
            size="large"
            htmlType="submit"
            className="mt-4 float-end"
            loading={isLoading}
            icon={<SaveOutlined />}
          >
            {t('common.save')}
          </Button>
        </form>
      </Card>
    </Spin>
  );
}

export default React.memo(VisaApplicationFormComponent);
